import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  VStack,
  Text,
  Grid,
  HStack,
  Divider,
  GridItem,
  Flex,
  Spacer,
  PopoverTrigger,
  Popover,
  Tooltip
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { setBackToWorklistPath } from '../../actions/ui';
import theme from '../../theme';
import './primary-global-navigation.scss';
import { styles } from './primary-global-navigation.styles';
import { navComponents } from './nav-icons';
import { OrganizationServiceline } from '../org-serviceline/org-serviceline';
import { UserProfile } from '../user-profile/user-profile';
import DashboardSelection from '../dashboard-selection/dashboard-selection-component';
import { BiomeIcon, IconName, LogoInitials } from '@codametrix/verdigris';
import { SubNav } from './sub-nav';
import { Logomark4C } from '@codametrix/verdigris';
import { AmplifyCore } from '@codametrix/ui-common';

export interface PrimaryGlobalNavigationProps {
  onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
  className?: string;
  disabled?: boolean;
  name?: string;
  profile_src?: string;
  tabColor?: string;
  context: CMx.Context;
  actionGroups: CMxAPI.NavActionGroup[];
  handleOrganizationChoice: (orgChoice: CMx.OrganizationFormChoice) => void;
  handleServiceLineChoice: (
    serviceLineChoice: CMx.ServiceLine,
    context: CMx.Context,
    actionGroups: CMxAPI.NavActionGroup[]
  ) => void;
  onSelection: (selction: CMxAPI.NavAction) => void;
  onLogout: () => void;
  activeOrg: string;
  activeUser: CMxAPI.User;
  serviceLine?: CMx.ServiceLine;
  openAccountSettings: () => void;
  openCapturedTime: () => void;
  listStatus: (activeTenantId: string) => void;
  updateStatus: (
    status: string,
    user: CMxAPI.User,
    context: CMx.Context
  ) => void;
  showDashboardOptions: boolean;
  getSubNavPaths: (userDetails: {
    tenantId: string;
    serviceLine: string;
  }) => void;
}

export const PrimaryGlobalNavigation: React.FC<PrimaryGlobalNavigationProps> = ({
  actionGroups,
  activeOrg: activeOrgDisplay,
  activeUser,
  context: cmxContext,
  context,
  handleOrganizationChoice,
  handleServiceLineChoice,
  listStatus,
  name,
  onLogout,
  onSelection,
  openAccountSettings,
  openCapturedTime,
  profile_src,
  serviceLine,
  showDashboardOptions,
  tabColor,
  updateStatus,
  className = '',
  disabled = false,
  getSubNavPaths = (userDetails: { tenantId: string; serviceLine: string }) =>
    {},
  ...props
}) => {
  const classes = styles(tabColor);
  const dispatch = useDispatch();

  const getActiveServiceLine = useCallback(() => {
    const serviceLineName = (serviceLine?.displayName || serviceLine?.name) ?? '';
    return serviceLineName;
  }, [serviceLine]);

  const [activeServiceLineName, setActiveServiceLineName] = useState<string>(
    getActiveServiceLine()
  );

  //region Parse out which analytics sections to display
  const subNavPaths = useSelector(
    (state: CMx.ApplicationState) => state.analytics.dashboardPaths
  );

  const tableauDashboardResourceId = context.activeContext?.resources.find(
    resource => resource.resourceName === 'tableau_dashboard'
  )?.resourceId;

  // Get outer sections (CMX Insights, Pre-Live Analytics) //
  const analyticsSections = context.activeContext?.resources.filter(
    resource =>
      resource.parentId === tableauDashboardResourceId &&
      AmplifyCore.benchEditable.verifyServiceLine(
        resource,
        context.activeContext?.serviceLineName ?? ''
      )
  );

  let analyticsSubnavs: {
    [key: string]: {
      name: string;
      dashboards: { dashboardName: string; dashboardResource: string }[];
    };
  } = {};

  if (analyticsSections) {
    // Get inner sections (Operational Analytics, Case Analytics,  etc...) //
    analyticsSections.forEach(section => {
      const subnavResources = context.activeContext?.resources.filter(
        resource =>
          resource.parentId === section.resourceId &&
          Object.keys(subNavPaths).includes(resource.resourceName)
      );

      if (!subnavResources) return;
      analyticsSubnavs[section.resourceName] = {
        name: JSON.parse(section.metadata).label,
        dashboards: subnavResources.map(resource => ({
          dashboardName: resource.label,
          dashboardResource: resource.resourceName
        }))
      };
    });
  }
  //endregion

  useEffect(() => {
    if (context.activeContext) {
      getSubNavPaths({
        tenantId: context.activeContext?.tenantId ?? '',
        serviceLine: context.activeContext?.serviceLineName ?? ''
      });
    }
  }, [context.activeContext, getSubNavPaths]);

  useEffect(() => {
    setActiveServiceLineName(getActiveServiceLine());
  }, [getActiveServiceLine, context]);

  useEffect(() => {
    actionGroups.forEach(actionGroup => {
      actionGroup.options.forEach(action => {
        if (action.active) {
          dispatch(setBackToWorklistPath(action));
        }
      });
    });
  }, [actionGroups, dispatch]);

  const selectionWrapper = (actionGroupOption: CMxAPI.NavAction) => {
    dispatch(setBackToWorklistPath(actionGroupOption));
    onSelection(actionGroupOption);
  };

  const [switcherOpened, setSwitcherOpened] = useState<boolean>(false);
  const [userProfileOpened, setUserProfileOpened] = useState<boolean>(false);
  const [dashboardSelectionOpened, setDashboardSelectionOpened] = useState<
    boolean
  >(false);

  const userInitials =
    activeUser?.firstName && activeUser?.lastName
      ? activeUser.firstName?.charAt(0) + ' ' + activeUser.lastName?.charAt(0)
      : '🧑‍⚕️';

  function handleSwitcherClick(): void {
    setSwitcherOpened(!switcherOpened);
  }

  function handleUserProfileClick(): void {
    setUserProfileOpened(!userProfileOpened);
  }

  const navItemComponentMap: Record<string, React.ComponentType<any>> = {
    Insights: DashboardSelection
  };

  const getComponentForNavItem = (navItemLabel: string) => {
    return navItemComponentMap[navItemLabel]; //|| DefaultComponent;
  };

  /* Currently only the Analytics section has subnavs. */
  const hasAnalyticsSubnavs =
    (analyticsSections && analyticsSections?.length > 1) ||
    (analyticsSubnavs &&
      Object.keys(analyticsSubnavs).length > 0 &&
      analyticsSubnavs[Object.keys(analyticsSubnavs)[0]]?.dashboards?.length >
        1);

  return (
    <Grid sx={classes['.mainVStack']} gridTemplateRows="min-content">
      <Box
        data-testid="primary-global-navigation-button"
        sx={classes['.mainGrid']}>
        <Popover
          closeOnBlur={true}
          placement="right"
          isOpen={switcherOpened}
          data-testid="global-navigation-switcher"
          onClose={() => {
            setSwitcherOpened(false);
          }}>
          {({ isOpen, onClose }) => (
            <>
              {/* @ts-ignore */}
              <PopoverTrigger>
                <Flex
                  sx={classes['.orgBox']}
                  data-testid="global-navigation-switcher-trigger"
                  className={`hoverCursor ${
                    switcherOpened ? 'popOverSelected' : ''
                  }`}
                  onClick={() => handleSwitcherClick()}>
                  <Box>
                    <Tooltip label={activeOrgDisplay} placement="left">
                      <Text sx={classes['.orgServiceText']}>
                        {activeOrgDisplay}
                      </Text>
                    </Tooltip>
                    <Tooltip
                      label={serviceLine?.displayName || serviceLine?.name}
                      placement="left">
                      <Text sx={classes['.orgServiceText']}>
                        {activeServiceLineName}
                      </Text>
                    </Tooltip>
                  </Box>
                  <Spacer />
                  <BiomeIcon
                    iconName={IconName.Chevron_Right}
                    iconSize="12"
                    titleText="More"
                  />
                </Flex>
              </PopoverTrigger>
              <OrganizationServiceline
                context={context}
                actionGroups={actionGroups}
                handleOrganizationChoice={handleOrganizationChoice}
                handleServiceLineChoice={handleServiceLineChoice}
                onSelection={onSelection}
                activeOrg={activeOrgDisplay}
                activeUser={activeUser}
                serviceLine={serviceLine}
                openAccountSettings={openAccountSettings}
                setSwitcherOpened={setSwitcherOpened}
              />
            </>
          )}
        </Popover>
      </Box>
      <VStack
        textStyle="Subtitle 2"
        sx={classes['.secondVStack']}
        spacing={theme.space[10]}>
        <VStack sx={classes['.avatarVstack']}>
          <Popover
            closeOnBlur={true}
            placement="right"
            isOpen={userProfileOpened}
            onClose={() => {
              setUserProfileOpened(false);
            }}>
            <PopoverTrigger>
              <Flex sx={classes['.avatarBox']}>
                <Avatar
                  name={userInitials}
                  src={profile_src}
                  sx={
                    userProfileOpened
                      ? classes['.selectedUserNameAvatar']
                      : classes['.usernNameAvatar']
                  }
                  onClick={() => {
                    handleUserProfileClick();
                  }}
                  data-testid={'userAvatar'}
                  className={`hoverCursor`}
                />
              </Flex>
            </PopoverTrigger>
            <UserProfile
              activeUser={activeUser}
              onLogout={onLogout}
              openAccountSettings={openAccountSettings}
              openCapturedTime={openCapturedTime}
              userInitials={userInitials}
              profile_src={profile_src}
              listStatus={listStatus}
              updateStatus={updateStatus}
            />
          </Popover>
        </VStack>
        {actionGroups
          ?.filter(actionGroup =>
            actionGroup.options.some(action => action.enabled)
          )
          .map(actionGroup =>
            actionGroup?.options?.map(actionGroupOption => {
              return (
                <HStack
                  key={actionGroupOption?.label}
                  width="100%"
                  onClick={() => {
                    selectionWrapper(actionGroupOption);
                  }}
                  className={`hoverCursor ${
                    actionGroupOption?.active ? 'navItem' : ''
                  }`}
                  data-testid={`${actionGroupOption?.label}tab`}>
                  <VStack
                    spacing={theme.space[2]}
                    sx={classes['.navItemVStack']}>
                    {hasAnalyticsSubnavs &&
                    actionGroupOption.navSubResources ? (
                      <SubNav
                        tabColor={tabColor}
                        selectionOpened={dashboardSelectionOpened}
                        setSelectionOpened={setDashboardSelectionOpened}
                        actionGroupOptionLabel={actionGroupOption?.label ?? ''}
                        childComponent={getComponentForNavItem(
                          actionGroupOption?.label ?? ''
                        )}
                      />
                    ) : (
                      <>
                        <Grid sx={classes['.iconWrapper']}>
                          {actionGroupOption?.icon
                            ? navComponents[actionGroupOption?.label ?? '']
                            : actionGroupOption?.icon}
                        </Grid>
                        <Text className="action-group-spacing" align={'center'}>
                          {actionGroupOption?.label}
                        </Text>
                      </>
                    )}
                  </VStack>
                  <Divider
                    orientation="vertical"
                    sx={classes['.dividerVertical']}
                  />
                </HStack>
              );
            })
          )}
      </VStack>
      <GridItem sx={classes['.navLogo']}>
        <Logomark4C titleText="Codametrix" />
      </GridItem>
    </Grid>
  );
};

PrimaryGlobalNavigation.displayName = 'PrimaryGlobalNavigation';
